import * as React from 'react'
import { useEffect, useRef } from 'react'
import { Link, Navigate, useNavigate } from 'react-router'

import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  RowData,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Project } from '../types'
import { ArrowDownCircleIcon, ArrowUpCircleIcon } from '@heroicons/react/16/solid'
import { getHealthStatusDot } from '../features/energy/utils'

interface ProjectTableProps {
  projects: Project[]
  error: string | null
  refreshData: () => void
  hoveredSiteId: number | null
  onRowHover: (siteId: number | null) => void
  onSiteClick: (siteId: number) => void
  onProjectHover?: (projectId: number | null) => void
}

declare module '@tanstack/react-table' {
  // allows us to define custom properties for our columns
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'text' | 'range' | 'select'
  }
}

function ProjectTable({
  projects,
  error,
  refreshData,
  hoveredSiteId,
  onRowHover,
  onSiteClick,
  onProjectHover
}: ProjectTableProps) {
  const rerender = React.useReducer(() => ({}), {})[1]
  // Add state to track if hover is from the map
  const [isHoverFromTable, setIsHoverFromTable] = React.useState(false);
  // Add navigation function for double-click
  const navigate = useNavigate();

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  )

  const columns = React.useMemo<ColumnDef<Project, any>[]>(
    () => [
      // {
      //   accessorKey: 'id',
      //   header: 'ID',
      //   cell: info => info.getValue(),
      // },
      {
        accessorKey: 'name',
        header: 'Project Name',
        cell: info => info.getValue(),
      },
      // {
      //   accessorKey: 'locality',
      //   header: 'Locality',
      //   cell: info => info.getValue() || 'N/A',
      // },
      {
        accessorKey: 'country',
        header: 'Country',
        cell: info => info.getValue() || 'N/A',
      },
      {
        accessorKey: 'total_generation_kw',
        header: 'Generation',
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined || value === 0) return '-';
          return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} kW`;
        },
      },
      {
        accessorKey: 'generation_health',
        header: 'Health',
        cell: info => {
          const value = info.getValue();
          return getHealthStatusDot(value as string);
        },
      },
      {
        accessorKey: 'total_storage_capacity_kwh',
        header: 'Storage',
        cell: info => {
          const value = info.getValue();
          if (value === null || value === undefined || value === 0) return '-';
          return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} kWh`;
        },
      },
      {
        accessorKey: 'storage_health',
        header: 'Health',
        cell: info => {
          const value = info.getValue();
          return getHealthStatusDot(value as string);
        },
      },
      {
        accessorKey: 'budget',
        header: 'Budget',
        cell: info => info.getValue() ? `$${info.getValue()}M` : '—',
        meta: {
          // filterVariant: 'range',
        },
      },
      // {
      //   accessorKey: 'sites',
      //   header: 'Sites',
      //   cell: info => (info.getValue() as string[]).length,
      // },
    ],
    []
  )

  const table = useReactTable({
    data: projects,
    columns,
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: false,
  })

  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});

  // Scroll to the hovered site only if hovered from the map
  useEffect(() => {
    if (hoveredSiteId && rowRefs.current[hoveredSiteId] && !isHoverFromTable) {
      rowRefs.current[hoveredSiteId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [hoveredSiteId, isHoverFromTable]);

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>
  }

  return (
    <div className="overflow-x-scroll">
      {/* <div className='text-sm font-semibold text-pacificblue-700 float-right'>{table.getPrePaginationRowModel().rows.length} Projects</div> */}
      <table className='table-auto w-full text-left'>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => {
                const isFirst = index === 0;

                return (
                  <th key={header.id} colSpan={header.colSpan} className={`pe-1 pb-2 text-pacificblue-900 ${isFirst ? 'ps-3' : ''}`}>
                    {header.isPlaceholder ? null : (
                      <>
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: <ArrowDownCircleIcon className='w-4 h-4 text-pacificblue-600 inline-block ml-1' />,
                            desc: <ArrowUpCircleIcon className='w-4 h-4 text-pacificblue-600 inline-block ml-1' />,
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                        {/* {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} />
                          </div>
                        ) : null} */}
                      </>
                    )}
                  </th>
                )
              })}
              <th className='pe-1 pb-2 text-pacificblue-900'>Sites</th>
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => {
            return (
              <tr
                key={row.id}
                className='rounded-full hover:bg-pacificblue-600/25 cursor-pointer'
                onMouseEnter={() => onProjectHover && onProjectHover(row.original.id)}
                onMouseLeave={() => onProjectHover && onProjectHover(null)}
                onClick={() => navigate(`project/${row.original.id}`)}
              >
                {row.getVisibleCells().map((cell, index) => {
                  const isFirst = index === 0;
                  const isLast = index === row.getVisibleCells().length - 1;

                  return (
                    <td
                      key={cell.id}
                      className={`pe-1 py-1 ${isFirst ? 'pl-3 rounded-l-full' : ''} ${isLast ? '' : ''}`}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  )
                })}
                <td className="text-center pe-1 rounded-r-full">
                  <span className="font-semibold">{row.original.sites.length}</span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* <div className="h-2" />
      <div className="flex items-center gap-2">
        <button
          className="border rounded p-1"
          onClick={() => table.setPageIndex(0)}
          disabled={!table.getCanPreviousPage()}
        >
          {'<<'}
        </button>
        <button
          className="border rounded p-1"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
        >
          {'<'}
        </button>
        <button
          className="border rounded p-1"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
        >
          {'>'}
        </button>
        <button
          className="border rounded p-1"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
          disabled={!table.getCanNextPage()}
        >
          {'>>'}
        </button>
        <span className="flex items-center gap-1">
          <div>Page</div>
          <strong>
            {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </strong>
        </span>
        <span className="flex items-center gap-1">
          | Go to page:
          <input
            type="number"
            min="1"
            max={table.getPageCount()}
            defaultValue={table.getState().pagination.pageIndex + 1}
            onChange={e => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0
              table.setPageIndex(page)
            }}
            className="border p-1 rounded w-16"
          />
        </span>
        <select
          value={table.getState().pagination.pageSize}
          onChange={e => {
            table.setPageSize(Number(e.target.value))
          }}
        >
          {[10, 20, 30, 40, 50].map(pageSize => (
            <option key={pageSize} value={pageSize}>
              Show {pageSize}
            </option>
          ))}
        </select>
      </div> */}
      {/* <div>
        <button onClick={() => rerender()}>Force Rerender</button>
      </div>
      <div>
        <button onClick={() => refreshData()}>Refresh Data</button>
      </div> */}
    </div>
  )
}

function Filter({ column }: { column: any }) {
  const columnFilterValue = column.getFilterValue()
  const { filterVariant } = column.columnDef.meta ?? {}

  return filterVariant === 'range' ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[0] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min`}
          className="w-24"
        />
        <DebouncedInput
          type="number"
          value={(columnFilterValue as [number, number])?.[1] ?? ''}
          onChange={value =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max`}
          className="w-24"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : filterVariant === 'select' ? (
    <select
      onChange={e => column.setFilterValue(e.target.value)}
      value={columnFilterValue?.toString()}
    >
      <option value="">All</option>
      <option value="N/A">N/A</option>
    </select>
  ) : (
    <DebouncedInput
      className="w-36"
      onChange={value => column.setFilterValue(value)}
      placeholder={`Search...`}
      type="text"
      value={(columnFilterValue ?? '') as string}
    />
  )
}

// A typical debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number
  onChange: (value: string | number) => void
  debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue)

  React.useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <input {...props} className='font-normal' value={value} onChange={e => setValue(e.target.value)} />
  )
}

export default ProjectTable;